import React, { useState } from 'react';
import { Send, User, Mail, Phone, MapPin, Calendar, X, Plus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import PassengerSelector from './PassengerSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingForm = () => {
  const { t } = useLanguage();
  const [tripType, setTripType] = useState('oneWay');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    fromCity: '',
    toCity: '',
    departureDate: '',
    returnDate: '',
    message: ''
  });

  const [passengers, setPassengers] = useState({
    adults: 1,
    children: 0,
    infants: 0
  });

  // Multi-city flights state
  const [multiCityFlights, setMultiCityFlights] = useState([
    { from: '', to: '', date: '' },
    { from: '', to: '', date: '' }
  ]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleMultiCityChange = (index, field, value) => {
    const newFlights = [...multiCityFlights];
    newFlights[index][field] = value;
    setMultiCityFlights(newFlights);
  };

  const addFlight = () => {
    if (multiCityFlights.length < 5) {
      setMultiCityFlights([...multiCityFlights, { from: '', to: '', date: '' }]);
    }
  };

  const removeFlight = (index) => {
    if (multiCityFlights.length > 2) {
      const newFlights = multiCityFlights.filter((_, i) => i !== index);
      setMultiCityFlights(newFlights);
    }
  };

  const validatePassengers = () => {
    if (passengers.adults < 1) {
      toast.error(t('minAdultError'));
      return false;
    }
    if (passengers.adults > 9) {
      toast.error(t('maxAdultError'));
      return false;
    }
    if (passengers.infants > passengers.adults) {
      toast.error(t('infantsExceedError'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassengers()) return;

    const bookingData = {
      ...formData,
      tripType,
      passengers
    };

    if (tripType === 'multiCity') {
      bookingData.flights = multiCityFlights;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, bookingData);
      
      toast.success(t('successMessage'), {
        duration: 5000,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        fromCity: '',
        toCity: '',
        departureDate: '',
        returnDate: '',
        message: ''
      });
      setPassengers({ adults: 1, children: 0, infants: 0 });
      setMultiCityFlights([
        { from: '', to: '', date: '' },
        { from: '', to: '', date: '' }
      ]);
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de la demande. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="reservation" className="py-24 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('bookYourTrip')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('bookingSubtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100">
          {/* Trip Type Selection */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              {t('tripType')} *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setTripType('oneWay')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  tripType === 'oneWay'
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="trip-type-oneway-btn"
              >
                {t('oneWay')}
              </button>
              <button
                type="button"
                onClick={() => setTripType('roundTrip')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  tripType === 'roundTrip'
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="trip-type-roundtrip-btn"
              >
                {t('roundTrip')}
              </button>
              <button
                type="button"
                onClick={() => setTripType('multiCity')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  tripType === 'multiCity'
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="trip-type-multicity-btn"
              >
                {t('multiCity')}
              </button>
            </div>
          </div>

          {/* From / To fields - One Way & Round Trip */}
          {(tripType === 'oneWay' || tripType === 'roundTrip') && (
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative">
                {/* From City */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('fromCity')} *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-500" size={20} />
                    <input
                      type="text"
                      name="fromCity"
                      value={formData.fromCity}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder={t('fromPlaceholder')}
                      data-testid="from-city-input"
                    />
                  </div>
                </div>

                {/* Arrow Icon (Desktop) */}
                <div className="hidden md:flex absolute left-1/2 top-12 transform -translate-x-1/2 z-10">
                  <div className="bg-white border-2 border-blue-500 rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                    <ArrowRight className="text-blue-500" size={18} />
                  </div>
                </div>

                {/* To City */}
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('toCity')} *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-red-500" size={20} />
                    <input
                      type="text"
                      name="toCity"
                      value={formData.toCity}
                      onChange={handleChange}
                      required
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder={t('toPlaceholder')}
                      data-testid="to-city-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dates - One Way */}
          {tripType === 'oneWay' && (
            <div className="mb-6">
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('departureDate')} *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    name="departureDate"
                    value={formData.departureDate}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    data-testid="departure-date-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Dates - Round Trip */}
          {tripType === 'roundTrip' && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('departureDate')} *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    name="departureDate"
                    value={formData.departureDate}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    data-testid="departure-date-input"
                  />
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('returnDate')} *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    name="returnDate"
                    value={formData.returnDate}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    data-testid="return-date-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Trip Details - Multi-City */}
          {tripType === 'multiCity' && (
            <div className="mb-6 space-y-4">
              {multiCityFlights.map((flight, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      {t('flight')} {index + 1}
                    </h3>
                    {multiCityFlights.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeFlight(index)}
                        className="text-red-600 hover:text-red-700 transition-colors duration-200"
                        data-testid={`remove-flight-${index}-btn`}
                      >
                        <X size={20} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t('from')} *
                      </label>
                      <input
                        type="text"
                        value={flight.from}
                        onChange={(e) => handleMultiCityChange(index, 'from', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="Ex: Kinshasa"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t('to')} *
                      </label>
                      <input
                        type="text"
                        value={flight.to}
                        onChange={(e) => handleMultiCityChange(index, 'to', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                        placeholder="Ex: Paris"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t('date')} *
                      </label>
                      <input
                        type="date"
                        value={flight.date}
                        onChange={(e) => handleMultiCityChange(index, 'date', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {multiCityFlights.length < 5 && (
                <button
                  type="button"
                  onClick={addFlight}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors duration-200"
                  data-testid="add-flight-btn"
                >
                  <Plus size={20} />
                  {t('addFlight')}
                </button>
              )}
            </div>
          )}

          {/* Passenger Selector */}
          <div className="mb-6">
            <PassengerSelector passengers={passengers} setPassengers={setPassengers} />
          </div>

          {/* Contact Information */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('contactUs')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('fullName')} *
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('namePlaceholder')}
                    data-testid="name-input"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('email')} *
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('emailPlaceholder')}
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="relative md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('phone')} *
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('phonePlaceholder')}
                    data-testid="phone-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t('yourRequest')}
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
              placeholder={t('messagePlaceholder')}
              data-testid="message-input"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-colors duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="submit-booking-btn"
          >
            <span>{isSubmitting ? 'Envoi en cours...' : t('sendRequest')}</span>
            {!isSubmitting && <Send className="group-hover:translate-x-1 transition-transform duration-300" size={20} />}
          </button>
        </form>
      </div>
    </section>
  );
};

export default BookingForm;
